/**
 * src/push/registerWebPush.ts — WEB Web Push registration (web-companion spec W4 / §2.5).
 *
 * Browser-only. Subscribes this browser to Web Push and registers the subscription with the
 * backend so assignment/comms notifications reach the user on any open client. Guarded by
 * isWebPushSupported() — a no-op on native and on browsers without push/notifications.
 *
 * Flow (enableWebPush, triggered by a user gesture — required by browsers):
 *   1. Notification.requestPermission() → must be 'granted'.
 *   2. Fetch the server VAPID public key (GET /api/push/vapid-key). If push isn't configured
 *      server-side, stop (fallback: in-app + email).
 *   3. pushManager.subscribe({ userVisibleOnly, applicationServerKey }) via the registered SW.
 *   4. POST the subscription (endpoint + p256dh + auth) to /api/push/subscribe.
 *
 * NEVER imports @smartbox/* (mobile invariant 1). No secrets logged.
 */
import { Platform } from 'react-native';
import { get, post, del } from '../api/client';
import type { VapidKeyResponse, PushSubscribeRequest } from '@beam/api-types';

const IS_WEB = Platform.OS === 'web';

export function isWebPushSupported(): boolean {
  return (
    IS_WEB &&
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  );
}

/** Current browser notification permission, or 'unsupported'. */
export function notificationPermission(): NotificationPermission | 'unsupported' {
  if (!isWebPushSupported()) return 'unsupported';
  return Notification.permission;
}

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  // Back the array with a concrete ArrayBuffer (not ArrayBufferLike) so it satisfies
  // PushSubscriptionOptionsInit.applicationServerKey (BufferSource) under strict TS lib types.
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/**
 * Enable Web Push for this browser. Returns a status the UI can surface.
 * Must be called from a user gesture (button press) so the permission prompt is allowed.
 */
export async function enableWebPush(): Promise<'enabled' | 'denied' | 'unsupported' | 'unavailable'> {
  if (!isWebPushSupported()) return 'unsupported';

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return 'denied';

  // The SW is registered by +html.tsx; wait until it's controlling the page.
  const reg = await navigator.serviceWorker.ready;

  const vapid = await get<VapidKeyResponse>('/api/push/vapid-key');
  if (!vapid.configured || !vapid.key) return 'unavailable';

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapid.key),
    });
  }

  const json = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return 'unavailable';

  const body: PushSubscribeRequest = {
    platform: 'web',
    endpoint: json.endpoint,
    p256dh: json.keys.p256dh,
    auth: json.keys.auth,
  };
  await post('/api/push/subscribe', body);
  return 'enabled';
}

/** Disable Web Push for this browser: unsubscribe locally + revoke server-side. */
export async function disableWebPush(): Promise<void> {
  if (!isWebPushSupported()) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  try {
    await sub.unsubscribe();
  } catch {
    // ignore — still revoke server-side
  }
  // Endpoint passed as a query param (the DELETE helper sends no body).
  await del(`/api/push/subscribe?endpoint=${encodeURIComponent(endpoint)}`).catch(() => undefined);
}
